import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import React, { useEffect } from "react";
import TitleBar from "../../layout/TitleBar";
import DefaultButtons from "../../common/buttons/DefaultButtons";
import { navigateTo } from "../../../utils/Navigation";
import { routesPaths } from "../../../Routes";
import { useHistory } from "react-router";
import { GetTranslation } from "../../../translations/GetTranslation";
import { useIntl } from "react-intl";
import { ExamSessionViewModel, initialExamSession } from "../../../model/ExamSessionViewModel";
import GridTextField from "../../common/customGrid/GridTextField";
import { useGetLocationsQuery } from "../../../api/rtk/LocationApi";
import { ValueType } from "react-select";
import CustomSelect, { OptionType } from "../../common/customSelect/CustomSelect";
import { GetTranslatedValueIntl } from "../../../model/Shared/TranslatedValue";
import { useGetTimeSlotStructureQuery } from "../../../api/rtk/TimeSlotStructureApi";
import { TimeSlotStructureLabels, TimeSlotStructureType } from "../../../model/TimeSlotStructure";
import TranslatedMessage from "../../translations/TranslatedMessage";
import BuilderRoomList, { RoomSelectionItem } from "./BuilderRoomList";
import { ExamType, ExamTemplateWithStructureIndex } from "../../../model/Exam";
import BuilderDateSelector from "./BuilderDateSelector";
import { ExamSessionBuilderHelper } from "../../../utils/ExamSessionBuilderHelper";
import { useSnackbar } from "notistack";
import { TranslationKey } from "../../../translations/TranslationKey";
import Alert from "@material-ui/lab/Alert";
import { useBuildExamSessionMutation } from "../../../api/rtk/ExamSessionApi";
import { LoadingSpinner } from "../../common/LoadingSpinner";
import { Build } from "@material-ui/icons";
import { getExamsIncludedInPublishedCourses } from "../../../api/ExamApi";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    gap: `${theme.spacing(2)}px`,
  },
  row: {
    gap: `${theme.spacing(2)}px`,
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(2)}px`,
  },
  withMargin: {
    marginBottom: theme.spacing(2),
  },
  buildButton: {
    "& .MuiButton-label": {
      display: "flex",
      gap: `${theme.spacing(1)}px`,
    },
  },
  buildButtonIcon: {
    width: "20px",
    height: "20px",
  }
}));

const ExamSessionBuilder: React.FC = () => {
  const history = useHistory();
  const intl = useIntl();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [examSession, setExamSession] = React.useState<ExamSessionViewModel>(initialExamSession);
  const [selectedLocationId, setSelectedLocationId] = React.useState<string | null>(null);
  const [selectedExamId, setSelectedExamId] = React.useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = React.useState<TimeSlotStructureType | null>(null);
  const [isLife, setIsLife] = React.useState<boolean>(false);
  const [exams, setExams] = React.useState<ExamTemplateWithStructureIndex[]>([]);
  const [isFetchingExams, setIsFetchingExams] = React.useState<boolean>(false);
  const [selectedRooms, setSelectedRooms] = React.useState<RoomSelectionItem[]>([]);
  const [selectedDays, setSelectedDays] = React.useState<Date[]>([]);
  const { data: allLocations, isFetching: isLocationsFetching } = useGetLocationsQuery();
  const { data: timeSlotStructure, isFetching: isFetchingTimeSlotStructure } = useGetTimeSlotStructureQuery();
  const [buildExamSession, { isLoading: isCreateExamSessionLoading }] = useBuildExamSessionMutation();

  useEffect(() => {
    setIsFetchingExams(true);
    getExamsIncludedInPublishedCourses()
      .then((exams) => {
        setExams(exams);
      })
      .finally(() => {
        setIsFetchingExams(false);
      });
  }, []);

  const handleSelectLocation = (selected: ValueType<OptionType>) => {
    const selectedValue = selected as OptionType;
    setSelectedLocationId(selectedValue.value);
    setExamSession((prevState) => ({
      ...prevState,
      locations: allLocations?.filter((location) => location.id === selectedValue.value) || [],
    }));
  };

  const handleSelectExam = (selected: ValueType<OptionType>) => {
    const selectedValue = selected as OptionType;
    setSelectedExamId(selectedValue.value);
    setSelectedTimeSlot(null); // Reset time slot when exam changes
  };

  const handleSelectTimeSlotStructure = (selected: ValueType<OptionType>) => {
    const selectedValue = selected as OptionType;
    setSelectedTimeSlot(selectedValue.value as TimeSlotStructureType);
  };

  const handleRoomSelectionChange = (selectedRooms: RoomSelectionItem[]) => {
    setSelectedRooms(selectedRooms);
  };

  useEffect(() => {
    if (selectedTimeSlot) {
      const exam = exams?.find((exam) => exam.id === selectedExamId);
      if (exam) {
        setExamSession((prevState) => ({
          ...prevState,
          exams: [exam],
        }));
      }
    } else {
      setExamSession((prevState) => ({
        ...prevState,
        exams: [],
      }));
    }
  }, [exams, selectedExamId, selectedTimeSlot]);

  const handleClose = () => {
    navigateTo(routesPaths.examSessions(), history);
  };

  const handleBuild = () => {
    const builder = new ExamSessionBuilderHelper({ ...examSession });
    const builtExamSession = builder
      .addIsLife(isLife)
      .addExamWithTypeStructure(exams!.find((exam) => exam.id === selectedExamId)!)
      .addLocation(allLocations!.find((location) => location.id === selectedLocationId)!)
      .addRooms(selectedRooms)
      .addDays(selectedDays, selectedTimeSlot!, timeSlotStructure!, selectedExamId!)
      .Build();
    if (!builtExamSession) {
      const errors = builder.getErrors();
      if (errors.length > 0) {
        errors.forEach((error) =>
          enqueueSnackbar(<TranslatedMessage id={error as TranslationKey} />, { variant: "error" })
        );
      }
      return;
    }
    buildExamSession(builtExamSession)
      .unwrap()
      .then((response) => {
        enqueueSnackbar(<TranslatedMessage id="examSession.result.examSessionCreated" />, { variant: "success" });
        navigateTo(routesPaths.examSession(response.data), history);
      });
  };

  const handleTextChange = (
    key: keyof ExamSessionViewModel,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.persist();
    setExamSession((prevState) => ({
      ...prevState,
      [key]: event.target.value,
    }));
  };

  const isLoading = isLocationsFetching || isFetchingExams || isFetchingTimeSlotStructure;
  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  const locationOptions = allLocations
    ? [...allLocations]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((location: { id: any; name: any }) => ({
          value: location.id,
          label: location.name,
        }))
    : [];

  const examOptions = exams
    ? [...exams]
        .sort((a, b) => GetTranslatedValueIntl(intl, a.name).localeCompare(GetTranslatedValueIntl(intl, b.name)))
        .map((exam) => ({
          value: exam.id,
          label: GetTranslatedValueIntl(intl, exam.name),
        }))
    : [];

  const selectedExamType = exams?.find((exam) => exam.id === selectedExamId)?.type || null;

  const timeSlotOptions = timeSlotStructure
    ? [
        ...Object.keys(TimeSlotStructureType).filter(
          (key) => selectedExamType === ExamType.TermPaper || key.includes(selectedExamType || "")
        ),
      ]
        .sort((a, b) =>
          GetTranslation(intl, TimeSlotStructureLabels[a as keyof typeof TimeSlotStructureLabels]).localeCompare(
            GetTranslation(intl, TimeSlotStructureLabels[b as keyof typeof TimeSlotStructureLabels])
          )
        )
        .map((key) => ({
          value: TimeSlotStructureType[key as keyof typeof TimeSlotStructureType],
          label: GetTranslation(intl, TimeSlotStructureLabels[key as keyof typeof TimeSlotStructureLabels]),
        }))
    : [];

  return (
    <Grid container={true} direction="column" spacing={4} className={classes.root}>
      <TitleBar title={GetTranslation(intl, "examSession.label.examSessionBuilder")} variant="h4">
        <DefaultButtons
          onClose={handleClose}
          onSave={handleBuild}
          isDeleteButtonVisible={false}
          isSaveButtonVisible={false}
          additionalButtons={
            <Button
              variant="contained"
              color="primary"
              onClick={handleBuild}
              disabled={
                !selectedLocationId ||
                !selectedExamId ||
                !selectedTimeSlot ||
                selectedRooms.length === 0 ||
                selectedDays.length === 0
              }
              className={classes.buildButton}
            >
              <Build className={classes.buildButtonIcon} /> <TranslatedMessage id="common.build" />
            </Button>
          }
        />
      </TitleBar>
      <Grid container={true} spacing={4} className={classes.withMargin}>
        <Alert severity="info">
          <TranslatedMessage id="examSession.alert.builderInformation" />
        </Alert>
      </Grid>
      <Grid container={true} spacing={4}>
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyDE")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={(e) => handleTextChange("sessionKeyDE", e)}
          value={examSession.sessionKeyDE}
        />

        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyEN")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={(e) => handleTextChange("sessionKeyEN", e)}
          value={examSession.sessionKeyEN}
        />
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyFR")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={(e) => handleTextChange("sessionKeyFR", e)}
          value={examSession.sessionKeyFR}
        />
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyIT")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={(e) => handleTextChange("sessionKeyIT", e)}
          value={examSession.sessionKeyIT}
        />
      </Grid>
      <Grid item={true} xs={12} sm={6} md={6} lg={6} xl={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isLife}
              onChange={(e) => setIsLife(e.target.checked)}
              style={{ margin: 0, padding: 9 }}
            />
          }
          label={
            <Typography variant="body2" color="textSecondary">
              <TranslatedMessage id="examSession.field.isLife" />
            </Typography>
          }
        />
      </Grid>
      <Grid container={true}>
        <Grid item={true} xs={12} sm={6} md={6} lg={6} xl={6} className={classes.column}>
          <CustomSelect
            required
            name={"location"}
            label={GetTranslation(intl, "location")}
            value={locationOptions.find((option) => option.value === selectedLocationId) || null}
            options={locationOptions}
            onChange={handleSelectLocation}
          />
          <CustomSelect
            required
            name={"exam"}
            label={GetTranslation(intl, "common.exam")}
            value={examOptions.find((option) => option.value === selectedExamId) || null}
            options={examOptions}
            onChange={handleSelectExam}
            isDisabled={!selectedLocationId}
          />
          <CustomSelect
            required
            name={"timneSlotStructure"}
            label={GetTranslation(intl, "common.timeSlotStructure")}
            value={timeSlotOptions.find((option) => option.value === selectedTimeSlot) || null}
            options={timeSlotOptions}
            onChange={handleSelectTimeSlotStructure}
            isDisabled={!selectedLocationId || !selectedExamId}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={6} lg={6} xl={6}>
          <BuilderDateSelector onChange={setSelectedDays} />
        </Grid>
      </Grid>

      {selectedLocationId && selectedTimeSlot && (
        <Grid container={true} spacing={2}>
          <BuilderRoomList
            location={allLocations?.find((l) => l.id === selectedLocationId)}
            selectedTimeSlot={selectedTimeSlot}
            onChange={handleRoomSelectionChange}
          />
        </Grid>
      )}
      {isCreateExamSessionLoading && <LoadingSpinner />}
    </Grid>
  );
};

export default ExamSessionBuilder;
